import React, { useState, useEffect } from 'react';

import { Paper, CircularProgress } from '@material-ui/core';
import { ResponsiveContainer, ComposedChart, Scatter, XAxis, YAxis, Tooltip, Bar, Text, ReferenceLine } from 'recharts';
import { NoGraphData } from 'screens/analytics/commonComponents';
import styled from 'styled-components';
import { StyledTypography } from 'styles/common';
import { truncate } from 'utils/lib';

import { GOAL_PROGRESS_CHART_COLORS } from 'constants/ProjectConstants';
import theme from 'constants/theme';

import { getAggregatedScore, getTimelineScore } from 'services/analytics/goal';

import { AbsCenterContainer } from '../../styles';
import { useGoalAnalytics } from '../GoalAnalyticsContext';

/**
 * @deprecated 06-08-2024
 */
const ProgressCheckinChart = () => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const {
    state: { currentState, selectedView, noData },
  } = useGoalAnalytics();

  const { expandedRowDetails } = selectedView;
  const { infoObj } = expandedRowDetails || null;

  const { selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods } = currentState;

  const fetchAggregatedData = () => {
    getAggregatedScore({
      cohort: selectedCohort,
      groupByKeys: selectedGroupByKey,
      splitByKeys: selectedSplitByKey,
      timeBounds: selectedTimeBoundPeriods,
    })
      .then((res) => setChartData(res))
      .catch((err) => {
        console.error('Error in fetching aggregated score', err);
      })
      .finally(() => setLoading(false));
  };

  const fetchTimelineData = (parentUuid) => {
    getTimelineScore({
      parentUuid,
      cohort: selectedCohort,
      groupByKeys: selectedGroupByKey,
      splitByKeys: selectedSplitByKey,
      timeBounds: selectedTimeBoundPeriods,
    })
      .then((res) => setChartData(res))
      .catch((err) => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (selectedCohort === null && selectedGroupByKey === null && selectedSplitByKey === null) return;

    setLoading(true);
    setChartData(null);

    if (infoObj === null) {
      fetchAggregatedData();
    } else {
      fetchTimelineData(infoObj.uuid);
    }
  }, [selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods, infoObj]);

  const HollowCircleShape = ({ cx, cy, color, iconSize, ...props }) => (
    <svg>
      <circle
        {...props}
        cx={cx - 0.5 || 0}
        cy={cy || 0}
        r={iconSize}
        stroke={color}
        strokeWidth={iconSize - 2}
        fill="#FFF"
      />
    </svg>
  );

  const CircleShape = ({ cx, cy, color, iconSize, ...props }) => (
    <svg>
      <circle {...props} cx={cx || 0} cy={cy || 0} r={iconSize} fill={color} />
    </svg>
  );

  const LabelText = ({ x, y, textColor, value }) => (
    <Text x={x} y={y} dy={20} offset={0} angle={-90}>
      {value}
    </Text>
  );

  const CustomizedLabelLeftY = ({ viewBox, textColor, value, iconColor, iconSize }) => {
    const { x, y } = viewBox;
    return (
      <svg>
        <CircleShape cx={x - 2} cy={y + 145} color={iconColor} iconSize={iconSize} />
        <LabelText x={x + 3} y={y + 110} textColor={textColor} value={value} />
      </svg>
    );
  };

  const CustomizedLabelRightY = ({ viewBox, textColor, value, iconColor, iconSize }) => {
    const { x, y } = viewBox;
    return (
      <svg>
        <HollowCircleShape cx={x + 55} cy={y + 147} color={iconColor} iconSize={iconSize} />
        <LabelText x={x + 60} y={y + 111} textColor={textColor} value={value} />
      </svg>
    );
  };

  const CustomTooltip = ({ name, active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipContainer>
          <StyledTypography weight={600} bottomGutter={0.5} variant="body2">
            {label}
          </StyledTypography>
          {payload.map((p) => (
            <StyledTypography variant="body2" color={p.fill}>
              <>{p.name === 'Progress' ? `${p.name}: ` : `# ${p.name}: `}</>
              <>{p.name === 'Progress' ? `${Number(p.value).toFixed(2)}%` : p.value}</>
            </StyledTypography>
          ))}
        </TooltipContainer>
      );
    }

    return null;
  };

  return (
    <Container elevation={0} style={{ border: `1px solid ${theme.palette.borders.lighterGrey}` }}>
      {loading ? (
        <AbsCenterContainer>
          <CircularProgress />
        </AbsCenterContainer>
      ) : !chartData ? (
        <AbsCenterContainer>
          <NoGraphData title="Goal Progress graph" subTitle="Error in fetching goal progress graph data" />
        </AbsCenterContainer>
      ) : chartData && chartData.length === 0 ? (
        <AbsCenterContainer>
          <NoGraphData title="Goal Progress graph" subTitle="No data found in this cohort and timeline" />
        </AbsCenterContainer>
      ) : (
        <ResponsiveContainer style={{ width: 'calc(100% - 5rem)' }}>
          <ComposedChart
            margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20,
            }}
            data={chartData}
          >
            {
              // <CartesianGrid stroke="#e9e9e9" horizontal={false} />
            }
            <XAxis
              dataKey="displayName"
              tickLine={false}
              // axisLine={{ stroke: '#c7c7c7' }}
              axisLine={false}
              tickSize="7"
              angle={-30}
              tick={{ fontSize: 13 }}
              textAnchor="end"
              interval={0}
              height={40}
              tickFormatter={(val) => truncate(/* val.toLocaleString().replace(/ /g, '') */ val, 10)}
            />
            <ReferenceLine alwaysShow strokeWidth={1.5} yAxisId="yAxis1" y={0} stroke="#c7c7c7" />

            <YAxis
              yAxisId="yAxis1"
              padding={{ top: 12 }}
              orientation="left"
              type="number"
              unit="%"
              tickLine={false}
              axisLine={{ stroke: '#c7c7c7' }}
              tickSize="14"
              tick={{ fontSize: 12 }}
              stroke="#999"
              domain={[0, 100]}
              width={65}
              tickFormatter={(val) => Number(val).toFixed(0)}
              label={
                <CustomizedLabelLeftY
                  value="Progress"
                  textColor="#555"
                  iconColor={GOAL_PROGRESS_CHART_COLORS.progress}
                  iconSize={6}
                />
              }
            />
            <YAxis
              yAxisId="yAxis2"
              padding={{ top: 12 }}
              orientation="right"
              type="number"
              dataKey="count"
              name="count"
              tickLine={false}
              axisLine={{ stroke: '#c7c7c7' }}
              tickSize="14"
              tick={{ fontSize: 12 }}
              stroke="#999"
              width={50}
              label={
                <CustomizedLabelRightY
                  value="Check-in"
                  textColor="#555"
                  iconColor={GOAL_PROGRESS_CHART_COLORS.checkIns}
                  iconSize={5}
                />
              }
            />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<CustomTooltip />} />
            <Bar
              yAxisId="yAxis1"
              name="Progress"
              barSize={17}
              dataKey="score"
              fill={GOAL_PROGRESS_CHART_COLORS.progress}
            />
            <Scatter
              yAxisId="yAxis2"
              name="Check-ins"
              dataKey="count"
              fill={GOAL_PROGRESS_CHART_COLORS.checkIns}
              shape={<HollowCircleShape color={GOAL_PROGRESS_CHART_COLORS.checkIns} iconSize={6} />}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Container>
  );
};

const Container = styled(Paper)`
  position: relative;
  min-height: calc(300px + 0.5rem);
  padding: 1rem 0.8rem;
  height: 100%;
`;

const TooltipContainer = styled.div`
  border: 1px solid ${(props) => props.theme.palette.text.secondaryLight};
  padding: 0.5rem;
  background: white;
`;

export default ProgressCheckinChart;
